// src/Pages/Blog/Blog.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../Firebase/firebaseConfig"; // Adjust the path as necessary
import { collection, getDocs } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import "./Blog.css"; // Ensure to import the CSS
import "@fortawesome/fontawesome-free/css/all.min.css";
import Spinner from 'react-bootstrap/Spinner'; // Import Spinner for loading state
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DEFAULT_FILTERS = { query: "", category: "all", year: "all" };

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true); // State for loading
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "blogs"));
        const blogsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Map 'imageUrl' to 'image'
        const updatedBlogs = blogsData.map(blog => ({
          ...blog,
          image: blog.imageUrl || null,
          images: blog.images || []
        }));
        setBlogs(updatedBlogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        toast.error(t("errorFetchingBlogs"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogs();
  }, [t]);

  // Helper function to get translated category name
  const getTranslatedCategory = (category) => {
    switch (category) {
      case "upcoming-events":
        return t("upcomingEvents");
      case "last-news":
        return t("lastNews");
      default:
        return t("uncategorized");
    }
  };

  const titleOf = (blog) =>
    blog.title?.[i18n.language] || blog.title?.en || Object.values(blog.title || {}).find(Boolean) || "";
  const snippetOf = (blog) =>
    blog.description?.[i18n.language] || blog.description?.en || Object.values(blog.description || {}).find(Boolean) || "";
  const categoryOf = (blog) => blog.category || "uncategorized";
  const yearOf = (blog) => (blog.date ? String(new Date(blog.date).getFullYear()) : "");

  const categories = useMemo(() => {
    const counts = {};
    blogs.forEach((b) => { counts[categoryOf(b)] = (counts[categoryOf(b)] || 0) + 1; });
    return counts;
  }, [blogs]);

  const years = useMemo(() => {
    const counts = {};
    blogs.forEach((b) => { const y = yearOf(b); if (y) counts[y] = (counts[y] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [blogs]);

  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const list = blogs.filter(
      (b) =>
        (filters.category === "all" || categoryOf(b) === filters.category) &&
        (filters.year === "all" || yearOf(b) === filters.year) &&
        (!q || titleOf(b).toLowerCase().includes(q) || snippetOf(b).toLowerCase().includes(q))
    );
    const time = (b) => (b.date ? new Date(b.date).getTime() : 0);
    return [...list].sort((a, b) => {
      if (sort === "oldest") return time(a) - time(b);
      if (sort === "az") return titleOf(a).localeCompare(titleOf(b), i18n.language);
      return time(b) - time(a);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogs, filters, sort, i18n.language]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () => setFilters(DEFAULT_FILTERS);
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  const Option = ({ name, value, label, count }) => (
    <label className={`blog-option ${filters[name] === value ? "is-active" : ""}`}>
      <input type="radio" name={name} checked={filters[name] === value} onChange={() => setFilter(name, value)} />
      <span className="blog-option__dot"></span>
      <span className="blog-option__label">{label}</span>
      <span className="blog-option__count">{count}</span>
    </label>
  );

  const activeChips = [
    filters.category !== "all" && { key: "category", label: getTranslatedCategory(filters.category) },
    filters.year !== "all" && { key: "year", label: filters.year },
    filters.query && { key: "query", label: `“${filters.query}”` },
  ].filter(Boolean);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" })
      : t("noDate");

  return (
    <div className="blog-page">
      <ToastContainer />

      {/* Hero band */}
      <header className="blog-hero">
        <div className="container">
          <span className="blog-hero__eyebrow">{t("lastNews")} &amp; {t("upcomingEvents")}</span>
          <h1 className="page-title">{t("ourBlog")}</h1>
          <div className="blog-hero__line"></div>
          <ul className="blog-hero__stats">
            <li><strong>{blogs.length}</strong> {t("posts", "posts")}</li>
            {Object.entries(categories).map(([c, n]) => (
              <li key={c}><strong>{n}</strong> {getTranslatedCategory(c)}</li>
            ))}
          </ul>
        </div>
      </header>

      <div className="container blog-layout">
        {/* Filter sidebar */}
        <aside className={`blog-sidebar ${showFilters ? "is-open" : ""}`} aria-label="Filters">
          <div className="blog-sidebar__head">
            <h2 className="blog-sidebar__title"><i className="fas fa-sliders-h"></i> {t("filters", "Filters")}</h2>
            {isFiltered && <button className="blog-clear" onClick={clearFilters}>{t("clearAll", "Clear all")}</button>}
          </div>

          <div className="blog-facet blog-facet--search">
            <div className="blog-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="form-control"
                placeholder={t("searchPosts", "Search posts…")}
                aria-label={t("searchPosts", "Search posts…")}
                value={filters.query}
                onChange={(e) => setFilter("query", e.target.value)}
              />
            </div>
          </div>

          <fieldset className="blog-facet">
            <legend>{t("category", "Category")}</legend>
            <Option name="category" value="all" label={t("all", "All")} count={blogs.length} />
            {Object.entries(categories).map(([c, n]) => (
              <Option key={c} name="category" value={c} label={getTranslatedCategory(c)} count={n} />
            ))}
          </fieldset>

          {years.length > 0 && (
            <fieldset className="blog-facet">
              <legend>{t("year", "Year")}</legend>
              <Option name="year" value="all" label={t("all", "All")} count={blogs.length} />
              {years.map(([y, n]) => (
                <Option key={y} name="year" value={y} label={y} count={n} />
              ))}
            </fieldset>
          )}
        </aside>

        {/* Results */}
        <main className="blog-results">
          <div className="blog-toolbar">
            <button className="blog-filter-toggle" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}>
              <i className="fas fa-sliders-h"></i> {t("filters", "Filters")}
              {isFiltered && <span className="blog-filter-toggle__dot"></span>}
            </button>

            <p className="blog-count"><strong>{visible.length}</strong> {t("posts", "posts")}</p>

            <label className="blog-sort">
              <span>{t("sortBy", "Sort by")}</span>
              <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="newest">{t("sortNewest", "Newest first")}</option>
                <option value="oldest">{t("sortOldest", "Oldest first")}</option>
                <option value="az">{t("sortAz", "Title A–Z")}</option>
              </select>
            </label>
          </div>

          {activeChips.length > 0 && (
            <div className="blog-chips">
              {activeChips.map((c) => (
                <button key={c.key} className="blog-chip" onClick={() => setFilter(c.key, DEFAULT_FILTERS[c.key])}>
                  {c.label} <i className="fas fa-times"></i>
                </button>
              ))}
            </div>
          )}

          {visible.length > 0 ? (
            <div className="blog-grid">
              {visible.map((blog) => (
                <article key={blog.id} className="blog-card" onClick={() => navigate(`/blog/${blog.id}`)}>
                  <div className="blog-image-container">
                    {blog.image ? (
                      <img
                        src={blog.image}
                        alt={titleOf(blog)}
                        className="blog-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          toast.error(t("imageLoadError"));
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="no-image-placeholder"><p>{t("noImageAvailable")}</p></div>
                    )}
                    <span className={`blog-tag blog-tag--${categoryOf(blog)}`}>{getTranslatedCategory(categoryOf(blog))}</span>
                  </div>
                  <div className="blog-content">
                    <time className="blog-date"><i className="far fa-calendar-alt"></i> {formatDate(blog.date)}</time>
                    <h3 className="blog-title">{titleOf(blog)}</h3>
                    <p className="blog-snippet">{snippetOf(blog)}</p>
                    <button
                      className="read-more-btn"
                      onClick={(e) => { e.stopPropagation(); navigate(`/blog/${blog.id}`); }}
                      aria-label={`Read more about ${titleOf(blog)}`}
                    >
                      {t("readMore")} <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="blog-empty">
              <i className="far fa-newspaper"></i>
              <p>{t("noBlogsAvailable", "No posts found")}</p>
              {isFiltered && <button className="btn btn-outline-primary" onClick={clearFilters}>{t("clearAll", "Clear all")}</button>}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Blog;
